// Sprint 6 (Phase 3, D49) — Render-time turn summary synthesis. Consumer
// updated by sprint turn-verdict-line (D5/D6): the original consumer,
// `TurnSummaryCard`, is deleted (D013-EC-180, unreachable in founder mode
// since Sprint 12); its live signals now feed `TurnCollapse`'s verdict
// line (`web/src/components/founder/TurnCollapse.tsx`).
//
// This is purely client-side and never persisted. After every completed
// assistant turn (delimited by a `system_event` with `subtype: 'result'`),
// this pure function returns a structured summary of:
//   • files modified (Edit/Write/MultiEdit `file_path`s)
//   • verifications (typecheck, tests, lint, build) inferred from Bash
//     headlines and tool_result text
//   • system notes (model switches, plan-mode toggles, rate-limit pauses)
//   • result status (success / error / stopped)
//
// Rules for when a turn carries NO verifications (`TurnCollapse` renders
// its "nothing to show" path, D11a/b — no empty state, not a hidden card):
//   • the turn had zero tool calls (pure conversational reply), OR
//   • the turn was interrupted (no `result` event reached us yet), OR
//   • the turn errored before any tool ran (we have a result but
//     hasToolCalls=false → suppression collapses both signals).
//
// Verification regexes are deliberately loose because Bash invocations vary
// (`npx vitest`, `npm test --silent`, etc.). Misclassification of WHICH
// category a command belongs to (typecheck vs. lint vs. build) is still
// acceptable — the consumer only reads `kind` to pick a label.
//
// The FAILURE predicate (`looksFailed`) is different (sprint
// turn-verdict-line, D6): its consumer is now `TurnCollapse`, the only
// sentence the founder reads to answer "did it work?" (D013-EC-180). The
// "misclassification is acceptable" fence above does NOT extend to
// `looksFailed` or `classifyBash`'s `npm test` match — both are anchored to
// counted evidence (`N failed`, `N errors` with N>0) instead of bare
// substrings (`fail`, `problem`), which false-positived on a clean `eslint`
// exit (`✖ 1 problem (0 errors, 1 warning)` → `0 errors`) and on any green
// test run whose suite/test NAMES contain the word "fail". What remains
// loose: category classification, and the `TurnSummary` shape / `exit`-turn
// coverage (D7, out of scope here).
//
// Sprint turn-end-signals-line (D013-EC-320, D4) — `parseMetric`'s
// MAGNITUDE was never anchored the way `looksFailed`'s VERDICT is: it ran
// `exec` over the whole `tool_result` body, so the first `N failed`/`N
// errors`/`N warnings` anywhere in the text won — a vitest `Test Files`
// line, an eslint per-file column number, a quoted source string inside a
// tsc diagnostic. D4 anchors each kind to its own summary-line pattern
// (one per producer) and fails closed (`null`) when no such line is
// recognised, rather than falling back to the old whole-body scan. The
// verdict (`looksFailed`) is untouched — only the number in parentheses
// was ever wrong. Also widened here (D5, `D013-EC-319`): the entry
// predicate below now enters `summarize()` for any `TURN_CLOSING_SUBTYPES`
// close (`result` OR `exit`), not `result` only, so a turn that ran its
// tools and closed on `exit` (process killed before the CLI emitted
// `result`) still gets a verdict instead of silent nothing.

import type { Message } from '../../types';
import { asToolUse } from '../messages';
import { longestCommonDir } from './path-utils';
import { TURN_CLOSING_SUBTYPES } from './turn-boundaries';

export type TurnStatus = 'success' | 'error' | 'stopped';
export type VerificationKind = 'typecheck' | 'tests' | 'lint' | 'build';

export interface TurnVerification {
  kind: VerificationKind;
  passed: boolean;
  /** Short metric, e.g. "47/47", "3 failed", "0 errors". null when unknown. */
  metric: string | null;
  /** Raw bash command (developer-mode reveal). */
  rawCommand: string | null;
}

export interface TurnFilesModified {
  count: number;
  commonDir: string | null;
  /** All distinct paths in order seen, capped at 50 for bounded memory. */
  paths: string[];
}

export interface TurnSummary {
  /** id of the system_event `result` that closed this turn. */
  resultMessageId: string;
  status: TurnStatus;
  filesModified: TurnFilesModified | null;
  verifications: TurnVerification[];
  systemNotes: string[];
  hasToolCalls: boolean;
}

export interface SynthesizeInput {
  messages: Message[];
}

export function synthesizeTurnSummaries(
  messages: Message[],
): Map<string, TurnSummary> {
  const out = new Map<string, TurnSummary>();
  let buffer: Message[] = [];
  for (const m of messages) {
    if (m.type === 'system' && isClosingEvent(m)) {
      const summary = summarize(m, buffer);
      if (summary) out.set(m.id, summary);
      buffer = [];
      continue;
    }
    buffer.push(m);
  }
  return out;
}

// D5 (`D013-EC-319`) — widened from `subtype === 'result'` to the full
// `TURN_CLOSING_SUBTYPES` set (imported, not re-inlined: `turn-boundaries.ts`
// already exports it and its own header comment was written to prevent
// exactly this duplication).
function isClosingEvent(m: Message): boolean {
  const c = m.content as { subtype?: unknown };
  return typeof c?.subtype === 'string' && TURN_CLOSING_SUBTYPES.has(c.subtype);
}

function summarize(resultMsg: Message, turn: Message[]): TurnSummary | null {
  const c = resultMsg.content as { result_subtype?: unknown };
  // NOT canonical (added at Gate 2, B3, sprint turn-end-signals-line, 2026-09-10):
  // `turn-boundaries.ts`'s `statusOf` (surfaced as `TurnMeta.status`) is the
  // canonical rendered turn status. This `status` is not `terminal_reason`-corrected
  // (no D1) and, since D5 widened `isClosingEvent` to `TURN_CLOSING_SUBTYPES`, this
  // function now runs on `exit` closes too — where it always returns 'stopped',
  // while `statusOf`'s `exit` branch correctly maps `finished` → 'success'. It has
  // no reader today (`ConversationList.tsx` consumes only `.verifications` off this
  // return value) and must not gain one without being reconciled against `statusOf`
  // first. See the matching note at `turn-boundaries.ts` `statusOf`. Fence log:
  // results doc §7 F-13 (NOT spec §7 F-8, a different, uncrossed fence).
  const status: TurnStatus = (() => {
    const sub = typeof c?.result_subtype === 'string' ? c.result_subtype : '';
    if (sub.includes('error')) return 'error';
    if (sub === 'success') return 'success';
    return 'stopped';
  })();

  // Pair tool_use with its tool_result inside this turn so the
  // verification heuristic can read both sides.
  const resultByToolUseId = new Map<string, Message>();
  for (const m of turn) {
    if (m.type === 'tool_result') {
      const tr = m.content as { tool_use_id?: unknown };
      const id = typeof tr?.tool_use_id === 'string' ? tr.tool_use_id : null;
      if (id) resultByToolUseId.set(id, m);
    }
  }

  const filePaths: string[] = [];
  const verifications: TurnVerification[] = [];
  const systemNotes: string[] = [];
  let hasToolCalls = false;

  for (const m of turn) {
    if (m.type === 'tool_use') {
      hasToolCalls = true;
      const tu = asToolUse(m);
      collectFiles(tu, filePaths);
      const v = detectVerification(tu, resultByToolUseId.get(tu.tool_use_id));
      if (v) verifications.push(v);
      continue;
    }
    if (m.type === 'system') {
      collectSystemNote(m, systemNotes);
    }
  }

  if (!hasToolCalls) return null;

  const filesModified = filePaths.length > 0
    ? {
        count: filePaths.length,
        commonDir: longestCommonDir(filePaths),
        paths: filePaths.slice(0, 50),
      }
    : null;

  return {
    resultMessageId: resultMsg.id,
    status,
    filesModified,
    verifications,
    systemNotes,
    hasToolCalls,
  };
}

const WRITE_TOOLS = new Set(['Edit', 'MultiEdit', 'Write']);

function collectFiles(
  tu: { name: string; input: Record<string, unknown> },
  out: string[],
) {
  if (!WRITE_TOOLS.has(tu.name)) return;
  const fp = tu.input.file_path;
  if (typeof fp !== 'string' || fp.length === 0) return;
  if (!out.includes(fp)) out.push(fp);
}

interface DetectedKind {
  kind: VerificationKind;
}

function classifyBash(cmd: string): DetectedKind | null {
  const c = cmd.toLowerCase();
  if (c.includes('npm run typecheck') || /\btsc\b/.test(c)) return { kind: 'typecheck' };
  if (
    // Anchored to a real invocation position (string start or right after a
    // shell separator/whitespace) — a `grep -rn "npm test" docs/` has "npm
    // test" preceded by a quote character, not a separator, so it no longer
    // matches (D6).
    /(^|[;&|]|\s)npm(\s+run)?\s+test\b/.test(c) ||
    /\bvitest\b/.test(c) ||
    /\bjest\b/.test(c) ||
    /\bplaywright\b/.test(c)
  ) {
    return { kind: 'tests' };
  }
  if (c.includes('npm run lint') || /\beslint\b/.test(c)) return { kind: 'lint' };
  if (c.includes('npm run build')) return { kind: 'build' };
  return null;
}

function detectVerification(
  tu: { name: string; input: Record<string, unknown>; tool_use_id: string },
  result: Message | undefined,
): TurnVerification | null {
  if (tu.name !== 'Bash') return null;
  const cmd = typeof tu.input.command === 'string' ? tu.input.command : '';
  const detected = classifyBash(cmd);
  if (!detected) return null;
  const tr = result?.content as { is_error?: unknown; content?: unknown } | null;
  const text = (() => {
    if (!tr) return '';
    const inner = tr.content;
    if (typeof inner === 'string') return inner;
    if (Array.isArray(inner)) {
      return inner
        .map((p) => {
          if (p && typeof p === 'object' && typeof (p as { text?: unknown }).text === 'string') {
            return (p as { text: string }).text;
          }
          return '';
        })
        .join('\n');
    }
    return '';
  })();
  const passed = (() => {
    if (tr?.is_error === true) return false;
    if (!result) return false; // still running → can't claim passed yet
    // Empty text + no error → assume pass.
    if (text.length === 0) return true;
    return !looksFailed(text, detected.kind);
  })();
  const metric = parseMetric(detected.kind, text);
  return {
    kind: detected.kind,
    passed,
    metric,
    rawCommand: cmd || null,
  };
}

function looksFailed(text: string, kind: VerificationKind): boolean {
  const t = text.toLowerCase();
  if (kind === 'typecheck' || kind === 'build') {
    return /\berror ts\d+/i.test(text) || /\d+\s+errors?\b/i.test(text);
  }
  if (kind === 'tests') {
    // D6: the bare `/\bfail(?:ed)?\b/` matched a test NAME containing
    // "fail" (e.g. "handles fail gracefully") on an all-green run —
    // anchored to an actual failure count instead.
    return /\b\d+\s+failed\b/.test(t) || /\bfailing\b/.test(t);
  }
  if (kind === 'lint') {
    // D6: the bare `/\bproblems?\b/` matched eslint's own summary line on
    // a CLEAN run (`✖ 1 problem (0 errors, 1 warning)`) — zero errors is
    // not a failure, so only a positive error count counts.
    return /\b[1-9]\d*\s+errors?\b/.test(t);
  }
  return false;
}

// D013-EC-320 / D4 — anchored to a recognised SUMMARY LINE per producer,
// never to the whole body. Exported for its own test (precedent:
// `translateMetric`, `TurnCollapse.tsx:146`, "exported for its test") and
// for the campaign outcomes script's `vite-node` harness (R10) — neither
// vendors a copy.
export function parseMetric(kind: VerificationKind, text: string): string | null {
  if (kind === 'tests') return parseTestsMetric(text);
  if (kind === 'typecheck' || kind === 'build') return parseTypecheckMetric(text);
  if (kind === 'lint') return parseLintMetric(text);
  return null;
}

// Shared "failed > passed(total) > passed" preference, applied to an
// ALREADY-ISOLATED summary line (never to the whole body) — the same
// order the pre-D4 code used, now scoped so it cannot collide with a
// body line that merely mentions a number.
function extractOrderedCount(line: string): string | null {
  const failed = /(\d+)\s+failed/i.exec(line);
  if (failed) return `${failed[1]} failed`;
  const total = /(\d+)\s+passed\s+\((\d+)\)/i.exec(line);
  if (total) return `${total[1]}/${total[2]}`;
  const passed = /(\d+)\s+passed/i.exec(line);
  if (passed) return `${passed[1]} passed`;
  return null;
}

function parseTestsMetric(text: string): string | null {
  // vitest — the `Tests` line, NOT the `Test Files` line that precedes it
  // (`Tests` requires no space between "Test" and "s"; "Test Files" has
  // one, so the two never collide).
  const vitestLine = /^\s*Tests\s+.*$/m.exec(text);
  if (vitestLine) return extractOrderedCount(vitestLine[0]);
  // jest
  const jestLine = /^\s*Tests:\s+.*$/m.exec(text);
  if (jestLine) return extractOrderedCount(jestLine[0]);
  // playwright — JS regex has no last-match operator; `matchAll`
  // collected into an array, indexed `[.length - 1]`, is the concrete
  // extraction form (D4, change 4).
  const pwMatches = [...text.matchAll(/^\s*\d+\s+(failed|passed)\b.*$/gm)];
  const lastMatch = pwMatches.at(-1);
  if (lastMatch) return extractOrderedCount(lastMatch[0]);
  return null;
}

function parseTypecheckMetric(text: string): string | null {
  const found = /^\s*Found\s+(\d+)\s+errors?\b.*$/m.exec(text);
  if (found) {
    const n = found[1];
    return `${n} error${n === '1' ? '' : 's'}`;
  }
  // No summary line recognised — fall back to counting distinct
  // `error TS\d+` occurrences rather than failing closed outright, since
  // that literal is tsc's own error marker (not a substring that can
  // appear inside a quoted diagnostic the way a bare `\d+\s+errors?` can).
  const occurrences = text.match(/error TS\d+/g);
  if (occurrences && occurrences.length > 0) {
    const n = occurrences.length;
    return `${n} error${n === 1 ? '' : 's'}`;
  }
  return null;
}

function parseLintMetric(text: string): string | null {
  const m =
    /^.*✖\s+\d+\s+problems?\s+\((\d+)\s+errors?,\s+(\d+)\s+warnings?\)\s*$/m.exec(text);
  if (!m) return null;
  const errorCount = Number(m[1]);
  const warnCount = Number(m[2]);
  // D6's preference order, preserved verbatim: prefer warnings when
  // errors are 0 — a clean run with warnings (`0 errors, 1 warning`)
  // must report the warning, not "0 errors".
  if (errorCount > 0) return `${errorCount} error${errorCount === 1 ? '' : 's'}`;
  if (warnCount > 0) return `${warnCount} warning${warnCount === 1 ? '' : 's'}`;
  return `${errorCount} error${errorCount === 1 ? '' : 's'}`;
}

function collectSystemNote(m: Message, notes: string[]) {
  const c = m.content as { subtype?: unknown };
  const sub = typeof c?.subtype === 'string' ? c.subtype : '';
  if (sub === 'config_changed') {
    const cc = m.content as {
      to?: { model?: unknown; effort?: unknown };
    };
    const model = typeof cc.to?.model === 'string' ? cc.to.model : null;
    if (model) notes.push(`Switched to ${humanModel(model)} mid-turn`);
    return;
  }
  // Plan-mode entries / rate-limit pauses are surfaced through other
  // subtypes the backend may add — keep this lookup permissive.
  if (sub === 'plan_mode_enter') notes.push('Entered plan mode');
  if (sub === 'plan_mode_exit') notes.push('Exited plan mode');
  if (sub === 'rate_limit_pause') notes.push('Paused on weekly cap');
}

function humanModel(model: string): string {
  if (model.includes('opus')) return 'Opus 4.6';
  if (model.includes('sonnet')) return 'Sonnet 4.5';
  if (model.includes('haiku')) return 'Haiku';
  return model;
}

